export type NegotiationGroup = "must" | "suggested" | "abandon";
export type NegotiationSalaryCapType = "hard" | "none" | "soft";

export type NegotiationTaskingPlayer = {
	contract: {
		exp: number;
		rookie: boolean;
	};
	mood: {
		user: {
			contractAmount: number;
			willing: boolean;
		};
	};
	ratings: {
		ovr: number;
		pot: number;
	};
};

export const getNegotiationAskingAmount = (p: NegotiationTaskingPlayer) =>
	p.mood.user.contractAmount / 1000;

export const canFitNegotiationContract = ({
	askingAmount,
	capSpace,
	minContractAmount,
	salaryCapType,
}: {
	askingAmount: number;
	capSpace: number;
	minContractAmount: number;
	salaryCapType: NegotiationSalaryCapType;
}) =>
	salaryCapType !== "hard" ||
	askingAmount <= Math.max(capSpace, minContractAmount);

export const getNegotiationGroup = ({
	capSpace,
	challengeNoRatings,
	minContractAmount,
	p,
	salaryCapType,
}: {
	capSpace: number;
	challengeNoRatings: boolean;
	minContractAmount: number;
	p: NegotiationTaskingPlayer;
	salaryCapType: NegotiationSalaryCapType;
}): NegotiationGroup => {
	const askingAmount = getNegotiationAskingAmount(p);
	const canFit = canFitNegotiationContract({
		askingAmount,
		capSpace,
		minContractAmount,
		salaryCapType,
	});

	if (p.contract.rookie) {
		return "must";
	}

	if (!p.mood.user.willing || !canFit) {
		return "abandon";
	}

	if (!challengeNoRatings && (p.ratings.ovr >= 65 || p.ratings.pot >= 75)) {
		return "must";
	}

	if (
		askingAmount <= Math.max(capSpace, minContractAmount) ||
		(!challengeNoRatings && (p.ratings.ovr >= 52 || p.ratings.pot >= 62))
	) {
		return "suggested";
	}

	return "abandon";
};

export const groupNegotiationPlayers = <P extends NegotiationTaskingPlayer>({
	capSpace,
	challengeNoRatings,
	minContractAmount,
	players,
	salaryCapType,
}: {
	capSpace: number;
	challengeNoRatings: boolean;
	minContractAmount: number;
	players: readonly P[];
	salaryCapType: NegotiationSalaryCapType;
}) => ({
	abandon: players.filter(
		(p) =>
			getNegotiationGroup({
				capSpace,
				challengeNoRatings,
				minContractAmount,
				p,
				salaryCapType,
			}) === "abandon",
	),
	must: players.filter(
		(p) =>
			getNegotiationGroup({
				capSpace,
				challengeNoRatings,
				minContractAmount,
				p,
				salaryCapType,
			}) === "must",
	),
	suggested: players.filter(
		(p) =>
			getNegotiationGroup({
				capSpace,
				challengeNoRatings,
				minContractAmount,
				p,
				salaryCapType,
			}) === "suggested",
	),
});
